import { Box, Card } from '@mui/material';
import { GridComponent, Group, Sort, Filter, Toolbar, ExcelExport, Reorder, Resize, ColumnChooser, Inject, FilterSettingsModel, Page, ExcelQueryCellInfoEventArgs, Search } from '@syncfusion/ej2-react-grids';
import { useTranslation } from 'react-i18next';
import { GetProductMovementsListPageRequest, ProductMovementListPageDTO, WarehouseVovClient } from '../../../../ApiOld/Api/ApiServerVov';
import GridComponentStatefull from '../../../Shared/Datagrid/GridComponentStatefull';
import { GridType } from '../../../../Api/UsersMicroserviceClient';
import { useEffect, useRef, useState } from 'react';
import { useApi } from '../../../../ApiOld/Api/useApi';
import { format } from 'date-fns';
import { useParams } from 'react-router';

interface ProductMovementsProps {
  productId: number | undefined;
}

export default function ProductMovements(props: Readonly<ProductMovementsProps>) {
  const { productId } = props;
  const { authorizedApi } = useApi(WarehouseVovClient);
  const [productMovements, setProductMovements] = useState<ProductMovementListPageDTO[] | undefined>();
  const grid = useRef<GridComponent>(null);
  const { t, i18n } = useTranslation();
  const params = useParams();

  useEffect(() => {
    if (productId) {
      authorizedApi.getProductMovementsListPage(new GetProductMovementsListPageRequest({ lang: params['lang'] ?? '', productId: productId })).then((r) => {
        setProductMovements(r ? r.productMovementListPage : []);

        if (grid.current) {
          grid.current.dataSource = r ? r.productMovementListPage ?? [] : [];
          grid.current.refresh();
        }
      });
    }
    else
      setProductMovements([]);
  }, [authorizedApi, productId, params]);

  let groupOptions = {
    showDropArea: true,
    captionTemplate: '<span class="groupItems" style="color:#FFC600"> ${key} - ${count} Úkolů</span>'
  };

  let filterOptions: FilterSettingsModel = {
    type: 'Excel',
    ignoreAccent: true
  };

  function toolbarClickExcel(props: any) {
    if (grid.current !== undefined && props.item.id.toString().endsWith('excelexport'))
      grid.current!.excelExport();
  }

  const excelQueryCellInfo = (args: ExcelQueryCellInfoEventArgs): void => {
  }

  function GridPriceTemplate(props: Readonly<ProductMovementListPageDTO>) {
    return <Box>{new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(props.totalPrice ?? 0)} Kč</Box>
  }

  const GridDateTemplate = (rowData: any) => <span>{format(new Date(rowData.datetime), 'dd.MM.yyyy HH:mm')}</span>;

  function GridStateTemplate(props: Readonly<ProductMovementListPageDTO>) {
    const { t } = useTranslation();
    switch (props.state) {
      case 'buy':
        return <>{t('warehouses.purchase')}</>;
      case 'in':
        return <>{t('warehouses.receipt')}</>;
      case 'block':
        return <>{t('warehouses.reservation')}</>;
      case 'out':
        return <>{t('warehouses.disbursement')}</>;
      case 'utility':
        return <>{t('warehouses.consumption')}</>;
      case 'write_off':
        return <>{t('warehouses.writeOff')}</>;
      default:
        return <>{props.state}</>;
    }
  };

  return <Box sx={{ maxWidth: '100vw' }}>
    <Card sx={{ height: '100%' }} elevation={3}>
      <GridComponentStatefull<ProductMovementListPageDTO>
        height={'calc(100vh - 372px)'}
        gridType={GridType.ProductMovements}
        data={productMovements ?? []}
        templateColumns={[
          { field: 'totalPrice', template: GridPriceTemplate },
          { field: 'datetime', template: GridDateTemplate },
          { field: 'state', template: GridStateTemplate },
        ]}
        canAdd={true}
        canChangeGroup canToolbarGrid canExcelExport
        id="grid-component-product-movements-list"
        allowSorting={true}
        enablePersistence={false}
        allowResizing={true}
        showColumnChooser={true}
        allowExcelExport={true}
        allowMultiSorting={true}
        allowGrouping={false}
        locale={i18n.language}
        excelQueryCellInfo={excelQueryCellInfo}
        rowHeight={30}
        allowReordering
        filterSettings={filterOptions}
        allowPaging={true}
        style={{ backgroundColor: 'white', overflow: 'auto' }}
        pageSettings={{ pageSize: 20, pageSizes: [10, 20, 30, 50, 100] }}
        toolbarClick={toolbarClickExcel}
        columnDataStateChange={(e) => { console.log(e) }}
        selectionSettings={{ checkboxMode: 'ResetOnRowClick' }}
        allowFiltering={true} groupSettings={groupOptions}
        ref={grid}

        searchLabel={t('warehouses.searchInProducts')}
        columnsXX={[
          { width: '50', minWidth: '50', headerText: '', type: 'checkbox', showInColumnChooser: false },
          { field: 'datetime', template: GridDateTemplate, headerText: t('Date'), width: '120', minWidth: '100' },
          { field: 'state', template: GridStateTemplate, headerText: t('warehouses.type'), width: '120', minWidth: '100' },
          { field: 'number', headerText: t('warehouses.number'), width: '120', minWidth: '100' },
          { field: 'warehouseName', headerText: t('warehouses.warehouse'), width: '120', minWidth: '100' },
          { field: 'userName', headerText: t('Employee'), width: '120', minWidth: '100' },
          { field: 'note', headerText: t('warehouses.notes'), width: '120', minWidth: '100' },
          { field: 'totalUnitsCount', headerText: t('warehouses.units'), width: '120', minWidth: '100' },
          { field: 'totalPrice', template: GridPriceTemplate, headerText: t('warehouses.totalPrice'), width: '120', minWidth: '100' }
        ]}
        moduleName={t('warehouses.products')}
        menuName={t('View')}
      >
        <Inject services={[Page, Sort, Filter, Search, Toolbar, ExcelExport, Reorder, Resize, ColumnChooser, Group]} />
      </GridComponentStatefull>
    </Card>
  </Box >
}
